import { get } from "lodash";
import { request, RequestInitWithRetry } from "../../lib/request";
import { ISortResult } from "../shared/common";

export type AccessPointsListParams = {
  limit: number,
  offset: number,
  search?: string,
  sort?: ISortResult
};

export type ExportAccessPointsListParams = {
  search?: string,
  sort?: ISortResult
};

export interface IAccessPoint {
  id: string,
  bssid: string,
  ssid: string,
  frequency: string,
  sampleCount: number,
  veryPoorPercentage: number,
  poorPercentage: number,
  moderatePercentage: number,
  goodPercentage: number,
  veryGoodPercentage: number,
  deviceCount: number,
  lastUsed: string,
  score: number
}

export type AccessPointsList = IAccessPoint[];

export type AccessPointsListAndCount = {
  list: AccessPointsList,
  totalCount: number
};

export function getAccessPointsList(params: AccessPointsListParams) {
  return async (options: RequestInitWithRetry): Promise<AccessPointsListAndCount> => {
    const input = {
      path: `/api/core/accessPoints/list`,
      qs: { limit: params.limit, offset: params.offset },
      body: {
        sort: get(params, 'sort'),
        search: get(params, 'search')
      },
      method: 'post'
    };
    const output = await request(input, options);
    return output;
  };
}

export interface IAccessPointSignalLevelTrend {
  date: number,
  sampleCount: number,
  score: number | null
}

export function getAccessPointSignalLevelTrends(params: { id: string, timeZone: string, from: number, to: number }) {
  return async (options: RequestInitWithRetry): Promise<IAccessPointSignalLevelTrend[]> => {
    const input = {
      path: `/api/core/accessPoint/signalLevelTrends`,
      qs: { id: params.id },
      body: { timeZone: params.timeZone, from: params.from, to: params.to },
      method: 'post'
    };
    return request(input, options);
  };
}

export type AccessPointInfo = {
  id: string,
  bssid: string,
  ssid: string,
  frequency: string | null
}

export function getAccessPointInfo(params: { id: string }) {
  return async (options: RequestInitWithRetry): Promise<AccessPointInfo | null> => {
    const input = {
      path: `/api/core/accessPoint/info`,
      qs: { id: params.id }
    };
    const output = await request(input, options);
    return output;
  };
}

export interface IAccessPointUniqueDevicesTrend {
  date: number,
  deviceCount: number | null
}

export function getAccessPointUniqueDevicesByTime(params: { id: string, timeZone: string, from: number, to: number }) {
  return async (options: RequestInitWithRetry): Promise<IAccessPointUniqueDevicesTrend[]> => {
    const input = {
      path: `/api/core/accessPoint/utilizationTrend`,
      qs: { id: params.id },
      body: { timeZone: params.timeZone, from: params.from, to: params.to },
      method: 'post'
    };
    return request(input, options);
  };
}

export interface IAccessPointRssiTrend {
  date: number,
  rssi: number | null
}

export function getAccessPointRssiTrend(params: { id: string, timeZone: string, from: number, to: number }) {
  return async (options: RequestInitWithRetry): Promise<IAccessPointRssiTrend[]> => {
    const input = {
      path: `/api/core/accessPoint/rssiTrend`,
      qs: { id: params.id },
      body: { timeZone: params.timeZone, from: params.from, to: params.to },
      method: 'post'
    };
    return request(input, options);
  };
}

export interface IAccessPointDroppedConnectionsTrend {
  date: number,
  count: number | null
}

export function getAccessPointDroppedConnectionsTrend(params: { bssid: string, ssid: string, timeZone: string, from: number, to: number }) {
  return async (options: RequestInitWithRetry): Promise<IAccessPointDroppedConnectionsTrend[]> => {
    const input = {
      path: `/api/core/accessPoint/droppedConnectionsTrend`,
      body: { bssid: params.bssid, ssid: params.ssid, timeZone: params.timeZone, from: params.from, to: params.to },
      method: 'post'
    };
    return request(input, options);
  };
}

export function exportAccessPointsListAction(requestOptions: RequestInitWithRetry) {
  return async (options: ExportAccessPointsListParams): Promise<string> => {
    const input = {
      path: `/api/core/accessPoints/exportList`,
      body: {
        sort: get(options, 'sort'),
        search: get(options, 'search'),
      },
      method: 'post'
    };
    return request(input, requestOptions);
  };
}
